import LogoutIcon from '@mui/icons-material/Logout';
import RestoreIcon from '@mui/icons-material/Restore';
import {Button, DialogActions, DialogContent, DialogContentText, DialogTitle} from '@mui/material';
import {useCallback, useEffect, useState} from 'react';
import {FormattedRelativeTime} from 'react-intl';
import {useExtendSession} from '../firebase/hooks/useExtendSession';
import {useTranslation} from '../i18n/hooks/useTranslation';
import {Translation} from '../i18n/Translation';
import {ComponentWithoutChildren} from '../react/types';
import {ResponsiveDialog} from '../responsive/ResponsiveDialog';
import {useLogout} from './hooks/useLogout';

let extensionWindowStartTimer: NodeJS.Timeout | undefined;
const clearExtensionWindowStartTimer = () => {
  if (extensionWindowStartTimer) {
    globalThis.clearTimeout(extensionWindowStartTimer);
    extensionWindowStartTimer = undefined;
  }
};

let extensionWindowEndTimer: NodeJS.Timeout | undefined;
const clearExtensionWindowEndTimer = () => {
  if (extensionWindowEndTimer) {
    globalThis.clearTimeout(extensionWindowEndTimer);
    extensionWindowEndTimer = undefined;
  }
};

export interface ExtendSessionModalProps {
  minutesBeforeExpiration: number;
  sessionExpirationTime: Date;
}

export const ExtendSessionModal: ComponentWithoutChildren<ExtendSessionModalProps> = ({
  minutesBeforeExpiration,
  sessionExpirationTime,
}: ExtendSessionModalProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const extendSession = useExtendSession();
  const logout = useLogout({restoreLocationOnNextLogin: true});
  const openAndStartWindowEndTimer = useCallback(() => {
    setOpen(true);
    // eslint-disable-next-line react-compiler/react-compiler
    extensionWindowEndTimer = globalThis.setTimeout(() => setOpen(false), sessionExpirationTime.getTime() - Date.now());
  }, [sessionExpirationTime, setOpen]);
  const extendSessionAndClose = useCallback(() => {
    setOpen(false);
    void extendSession();
  }, [extendSession, setOpen]);
  const logoutAndClose = useCallback(() => {
    setOpen(false);
    void logout();
  }, [logout, setOpen]);
  const {translate} = useTranslation('auth');

  useEffect(() => {
    clearExtensionWindowStartTimer();
    clearExtensionWindowEndTimer();
    const nowMillis = Date.now();
    const sessionExtensionTimeMillis = sessionExpirationTime.getTime() - minutesBeforeExpiration * 60 * 1_000;
    const millisUntilExtension = sessionExtensionTimeMillis - nowMillis;
    const millisUntilExpiration = sessionExpirationTime.getTime() - nowMillis;
    if (millisUntilExtension > 0) {
      // The start of the extension window is in the future,
      // so we need to set the timer to display the extension modal when the extension window starts
      extensionWindowStartTimer = globalThis.setTimeout(openAndStartWindowEndTimer, millisUntilExtension);
    } else if (millisUntilExpiration > 0) {
      // We're already inside the extension window, but the extension has not yet expired,
      // so we can just display the extnesion modal immediately
      globalThis.setTimeout(openAndStartWindowEndTimer, 0);
    }
    return () => {
      clearExtensionWindowStartTimer();
      clearExtensionWindowEndTimer();
    };
  }, [minutesBeforeExpiration, openAndStartWindowEndTimer, sessionExpirationTime]);

  return (
    <ResponsiveDialog open={open}>
      <DialogTitle>{translate('auth:session.extend.dialog.title')}</DialogTitle>
      <DialogContent sx={{minWidth: '25em'}}>
        <FormattedRelativeTime
          updateIntervalInSeconds={1}
          // eslint-disable-next-line react-hooks/purity
          value={(sessionExpirationTime.getTime() - Date.now()) / 1_000}
        >
          {(time) => (
            <Translation keys="auth:session.extend.dialog.description" options={{replace: {time}}}>
              {(content) => <DialogContentText>{content}</DialogContentText>}
            </Translation>
          )}
        </FormattedRelativeTime>
      </DialogContent>
      <DialogActions>
        <Translation keys="auth:logout">
          {(content) => (
            <Button color="inherit" onClick={logoutAndClose} startIcon={<LogoutIcon />} variant="contained">
              {content}
            </Button>
          )}
        </Translation>
        <Translation keys="auth:session.extend.dialog.extendSessionButton">
          {(content) => (
            <Button color="primary" onClick={extendSessionAndClose} startIcon={<RestoreIcon />} variant="contained">
              {content}
            </Button>
          )}
        </Translation>
      </DialogActions>
    </ResponsiveDialog>
  );
};
